import React from 'react'
import { motion } from 'framer-motion'
import type { ChatMessage } from '../types'
import type {
  CallEndedEventData,
  CallRecordingEventData,
  CallStartedEventData,
  ConversationProcessingResultEventData,
  EmailMessageReceivedEventData,
  EmailMessageSentEventData,
} from '@super/shared-types'
import {
  TextMessage,
  CallMessage,
  RecordingMessage,
  SystemMessage,
  ConversationProcessingSummaryMessage,
  ActionExecutionMessage,
} from './MessageTypes'
import type { SessionDetailsFocus } from './SessionDetailsDrawer/focus'
interface MessageProps {
  message: ChatMessage
  currentUserId?: string
  index?: number
  threadId?: string
  companyId?: string
  onEmailClick?: (
    emailData: EmailMessageReceivedEventData | EmailMessageSentEventData,
  ) => void
  onSessionDetailsClick?: (
    sessionId: string,
    focus?: SessionDetailsFocus,
  ) => void
  isSuperAdmin?: boolean
}

export const Message: React.FC<MessageProps> = ({
  message,
  currentUserId,
  companyId,
  threadId,
  onEmailClick,
  onSessionDetailsClick,
  isSuperAdmin,
}) => {
  // Don't render hidden messages
  if (message.hidden) {
    return null
  }

  const isOutgoing = message.sender.id === currentUserId

  // Check if this is a recently sent message (within last 5 seconds)
  const isRecentMessage =
    Date.now() - new Date(message.createdAt).getTime() < 5000

  const messageContent = () => {
    switch (message.type) {
      case 'text':
        return (
          <TextMessage
            message={message}
            isOutgoing={isOutgoing}
            threadId={threadId}
            companyId={companyId}
            sessionId={message.sessionId}
            onSessionDetailsClick={
              isSuperAdmin ? onSessionDetailsClick : undefined
            }
            onEmailClick={onEmailClick}
          />
        )

      case 'call_start':
        const callStartData = message.data as CallStartedEventData
        const isOutboundCall = callStartData?.direction === 'OUTBOUND'
        return (
          <CallMessage
            isCallStart={true}
            createdAt={message.createdAt}
            threadId={threadId}
            messageId={message.id}
            companyId={companyId}
            direction={callStartData?.direction}
            originatingCaller={
              isOutboundCall ? 'AI Agent' : callStartData?.from
            }
            sessionId={message.sessionId}
            onSessionDetailsClick={
              isSuperAdmin ? onSessionDetailsClick : undefined
            }
          />
        )

      case 'call_end':
        const callEndData = message.data as CallEndedEventData
        return (
          <CallMessage
            isCallStart={false}
            duration={callEndData?.duration}
            createdAt={message.createdAt}
            threadId={threadId}
            messageId={message.id}
            companyId={companyId}
            direction={callEndData?.conference?.direction}
            sessionId={message.sessionId}
            onSessionDetailsClick={
              isSuperAdmin ? onSessionDetailsClick : undefined
            }
          />
        )

      case 'recording':
        const recordingData = message.data as CallRecordingEventData
        return (
          <RecordingMessage
            recordingUrl={recordingData.recordingUrl}
            recordingDuration={recordingData.recordingDuration}
            createdAt={message.createdAt}
            conference={recordingData.conference}
            threadId={threadId}
            messageId={message.id}
            companyId={companyId}
            sessionId={message.sessionId}
            onSessionDetailsClick={
              isSuperAdmin ? onSessionDetailsClick : undefined
            }
          />
        )

      case 'conversation_processing_summary':
        const processingData =
          message.data as ConversationProcessingResultEventData
        return (
          <ConversationProcessingSummaryMessage
            data={processingData}
            threadId={threadId}
            messageId={message.id}
            companyId={companyId}
            sessionId={message.sessionId}
            onSessionDetailsClick={
              isSuperAdmin ? onSessionDetailsClick : undefined
            }
          />
        )

      case 'action_execution':
        return (
          <ActionExecutionMessage
            message={message}
            threadId={threadId}
            messageId={message.id}
            companyId={companyId}
            sessionId={message.sessionId}
            onSessionDetailsClick={
              isSuperAdmin ? onSessionDetailsClick : undefined
            }
          />
        )

      case 'system':
        return (
          <SystemMessage
            message={message}
            threadId={threadId}
            companyId={companyId}
            sessionId={message.sessionId}
            onSessionDetailsClick={
              isSuperAdmin ? onSessionDetailsClick : undefined
            }
          />
        )

      default:
        return (
          <TextMessage
            message={message}
            isOutgoing={isOutgoing}
            threadId={threadId}
            companyId={companyId}
            sessionId={message.sessionId}
            onSessionDetailsClick={
              isSuperAdmin ? onSessionDetailsClick : undefined
            }
            onEmailClick={onEmailClick}
          />
        )
    }
  }

  return (
    <motion.div
      initial={isRecentMessage ? { opacity: 0 } : false}
      animate={{ opacity: 1 }}
      transition={{
        duration: 0.3,
        ease: 'easeOut',
      }}
      className="group relative"
    >
      {messageContent()}
    </motion.div>
  )
}
